import { useEffect, useMemo } from "react";
import { FILTER_VALUES } from "@/constants/filters";
import { getTimeRangeDateRange } from "@/constants/timeRange";
import useUtilizationDeepDiveStore from "@/context/utilization/useUtilizationDeepDiveStore";
import { useApiKeys } from "@/hooks/api-keys";
import { useCustomerScopedQuery } from "@/hooks/useCustomerScopedQuery";
import { useInvoices } from "@/hooks/useInvoices";
import { useMeteredUsage } from "@/hooks/usage";
import { useUsagePlan } from "@/hooks/useUsagePlan";
import { services } from "@/services/registry";
import { parseAuditLogTimestamp } from "@/utils/auditLogs";
import { getHttpResponseMessage } from "@/utils/httpError";
import { getConsumptionDateRange } from "@/utils/usageDateRange";
import { DONUT_COLORS, keyUsageTotalsFromMetered, numberValue, shortApiKey, trendPointsFromAuditLogs, trendPointsFromUsageQuery } from "./utils";
function useUtilizationDeepDiveData({ activeProductCode, contractId, contractExpiryDate, contractStartDate, customerIdOverride, isExpiredContract, enabled = true }) {
    const { filters, setSelectedApiKey } = useUtilizationDeepDiveStore();
    const { selectedPeriod, selectedApiKey } = filters;
    const periodRange = useMemo(()=>getTimeRangeDateRange(selectedPeriod, isExpiredContract ? contractExpiryDate : undefined), [
        contractExpiryDate,
        isExpiredContract,
        selectedPeriod
    ]);
    const { usagePlan, isLoadingContracts } = useUsagePlan({
        contractId,
        enabled
    });
    const { data: apiKeysData, isLoading: apiKeysLoading } = useApiKeys(activeProductCode, enabled && !!activeProductCode, customerIdOverride);
    const { invoices } = useInvoices(undefined, enabled && !!contractId && !isExpiredContract);
    const contractInvoices = useMemo(()=>invoices.filter((invoice)=>invoice.contractId === contractId), [
        contractId,
        invoices
    ]);
    const consumptionDateRange = useMemo(()=>getConsumptionDateRange(contractInvoices, isExpiredContract ? contractExpiryDate?.slice(0, 10) : undefined, contractStartDate), [
        contractExpiryDate,
        contractInvoices,
        contractStartDate,
        isExpiredContract
    ]);
    const { data: consumptionUsageData } = useMeteredUsage(contractId, consumptionDateRange.startDate, consumptionDateRange.endDate, undefined, enabled && !isExpiredContract);
    const activeApiKeys = useMemo(()=>apiKeysData?.items?.filter((apiKey)=>apiKey.status === "Active") ?? [], [
        apiKeysData?.items
    ]);
    const filterableApiKeys = useMemo(()=>isExpiredContract ? apiKeysData?.items ?? [] : activeApiKeys, [
        activeApiKeys,
        apiKeysData?.items,
        isExpiredContract
    ]);
    const selectableApiKeys = useMemo(()=>filterableApiKeys.slice(0, 20), [
        filterableApiKeys
    ]);
    useEffect(()=>{
        if (apiKeysLoading || selectedApiKey === FILTER_VALUES.ALL) {
            return;
        }
        const selectedStillExists = selectableApiKeys.some((apiKey)=>apiKey.id === selectedApiKey);
        if (!selectedStillExists) {
            setSelectedApiKey(FILTER_VALUES.ALL);
        }
    }, [
        apiKeysLoading,
        selectableApiKeys,
        selectedApiKey,
        setSelectedApiKey
    ]);
    const selectedApiKeyParam = selectedApiKey === FILTER_VALUES.ALL ? undefined : selectedApiKey;
    const meteredUsageQuery = useCustomerScopedQuery({
        queryKey: [
            "utilization-deep-dive",
            "metered",
            contractId,
            periodRange.startDate,
            periodRange.endDate,
            selectedApiKeyParam
        ],
        queryFn: ({ customerId })=>{
            if (!contractId) {
                throw new Error("Contract ID is required");
            }
            return services.usageService.getMeteredUsage(contractId, periodRange.startDate, periodRange.endDate, selectedApiKeyParam, customerId);
        },
        enabled: enabled && !!contractId,
        staleTime: 5 * 60 * 1000,
        refetchInterval: 5 * 60 * 1000
    });
    const usageOverTimeQuery = useCustomerScopedQuery({
        queryKey: [
            "utilization-deep-dive",
            "usage-over-time",
            contractId,
            selectedPeriod,
            periodRange.startDate,
            periodRange.endDate,
            selectedApiKeyParam
        ],
        queryFn: ({ customerId })=>{
            if (!contractId) {
                throw new Error("Contract ID is required");
            }
            if (selectedPeriod === "24h") {
                return services.usageService.getHourlyUsage(contractId, periodRange.endDate, selectedApiKeyParam, customerId);
            }
            return services.usageService.getDailyAggregatedUsage(contractId, periodRange.startDate, periodRange.endDate, selectedApiKeyParam, customerId);
        },
        enabled: enabled && !!contractId,
        staleTime: 5 * 60 * 1000,
        refetchInterval: 5 * 60 * 1000
    });
    const auditLogsQuery = useCustomerScopedQuery({
        queryKey: [
            "utilization-deep-dive",
            "audit-logs",
            activeProductCode,
            customerIdOverride,
            periodRange.startDate,
            periodRange.endDate,
            selectedApiKeyParam
        ],
        queryFn: ({ customerId })=>services.auditLogsService.getAuditLogs(periodRange.startDate, periodRange.endDate, selectedApiKeyParam, customerIdOverride ?? customerId, 200),
        enabled: enabled && !!activeProductCode,
        staleTime: 5 * 60 * 1000,
        refetchInterval: 5 * 60 * 1000
    });
    const productApiKeyIds = useMemo(()=>{
        return new Set(apiKeysData?.items?.map((apiKey)=>apiKey.id) ?? []);
    }, [
        apiKeysData?.items
    ]);
    const combinedAuditLogs = useMemo(()=>{
        if (!auditLogsQuery.data || productApiKeyIds.size === 0) {
            return [];
        }
        return (auditLogsQuery.data.logs || []).filter((log)=>productApiKeyIds.has(log.apiKeyId)).sort((a, b)=>{
            return parseAuditLogTimestamp(b.requestTime).getTime() - parseAuditLogTimestamp(a.requestTime).getTime();
        });
    }, [
        auditLogsQuery.data,
        productApiKeyIds
    ]);
    const keyNameMap = useMemo(()=>{
        const map = new Map();
        filterableApiKeys.forEach((apiKey)=>{
            if (apiKey.name) {
                map.set(apiKey.id, apiKey.name);
            }
        });
        return map;
    }, [
        filterableApiKeys
    ]);
    const keyUsageTotals = useMemo(()=>{
        return keyUsageTotalsFromMetered(meteredUsageQuery.data?.apiKeyTotals);
    }, [
        meteredUsageQuery.data?.apiKeyTotals
    ]);
    const trendPoints = useMemo(()=>{
        const usagePoints = trendPointsFromUsageQuery({
            usageData: usageOverTimeQuery.data,
            selectedPeriod,
            startDate: periodRange.startDate,
            endDate: periodRange.endDate
        });
        const hasUsageQueryData = usagePoints.some((point)=>point.usageValue > 0);
        return hasUsageQueryData ? usagePoints : trendPointsFromAuditLogs({
            auditLogs: combinedAuditLogs,
            selectedPeriod,
            startDate: periodRange.startDate,
            endDate: periodRange.endDate
        });
    }, [
        combinedAuditLogs,
        periodRange,
        selectedPeriod,
        usageOverTimeQuery.data
    ]);
    const peakPoint = useMemo(()=>{
        if (trendPoints.length === 0) {
            return null;
        }
        return trendPoints.reduce((peak, point)=>{
            return point.usageValue > peak.usageValue ? point : peak;
        }, trendPoints[0]);
    }, [
        trendPoints
    ]);
    const totalUsageFromMetered = numberValue(meteredUsageQuery.data?.totalUsageValue);
    const totalUsageFromFallback = keyUsageTotals.reduce((sum, item)=>sum + item.totalUsageValue, 0);
    const totalUsageValue = totalUsageFromMetered > 0 ? totalUsageFromMetered : totalUsageFromFallback;
    const usageUnit = totalUsageFromMetered > 0 ? meteredUsageQuery.data?.usageUnit || "Bytes" : "BYTES";
    const totalRequests = getTotalRequestsFromUsageOverTime(usageOverTimeQuery.data) ?? numberValue(meteredUsageQuery.data?.overallRecordCount);
    const avgDailyUsageValue = periodRange.dayCount > 0 ? totalUsageValue / periodRange.dayCount : 0;
    const avgHourlyUsageValue = periodRange.hourCount > 0 ? totalUsageValue / periodRange.hourCount : 0;
    const topTenKeys = useMemo(()=>{
        return [
            ...keyUsageTotals
        ].sort((left, right)=>right.totalUsageValue - left.totalUsageValue).slice(0, 10);
    }, [
        keyUsageTotals
    ]);
    const donutSegments = useMemo(()=>{
        if (totalUsageValue <= 0 || topTenKeys.length === 0) {
            return [];
        }
        const topSegments = topTenKeys.slice(0, 10).map((item, index)=>({
                label: keyNameMap.get(item.apiKeyId) || shortApiKey(item.apiKeyId),
                value: item.totalUsageValue,
                percentage: item.totalUsageValue / totalUsageValue * 100,
                color: DONUT_COLORS[index % DONUT_COLORS.length]
            }));
        const usedValue = topSegments.reduce((sum, segment)=>sum + segment.value, 0);
        const remainingValue = Math.max(totalUsageValue - usedValue, 0);
        if (remainingValue > 0) {
            topSegments.push({
                label: "Others",
                value: remainingValue,
                percentage: remainingValue / totalUsageValue * 100,
                color: DONUT_COLORS[DONUT_COLORS.length - 1]
            });
        }
        return topSegments;
    }, [
        keyNameMap,
        topTenKeys,
        totalUsageValue
    ]);
    const selectedApiKeyDetails = useMemo(()=>{
        if (selectedApiKey === FILTER_VALUES.ALL) {
            return undefined;
        }
        return selectableApiKeys.find((apiKey)=>apiKey.id === selectedApiKey);
    }, [
        selectableApiKeys,
        selectedApiKey
    ]);
    const lastUsedAt = useMemo(()=>{
        let latest = null;
        for (const log of combinedAuditLogs){
            if (!log.requestTime) {
                continue;
            }
            const parsed = parseAuditLogTimestamp(log.requestTime);
            if (Number.isNaN(parsed.getTime())) {
                continue;
            }
            if (!latest || parsed > latest) {
                latest = parsed;
            }
        }
        return latest;
    }, [
        combinedAuditLogs
    ]);
    const isFiltersLoading = isLoadingContracts || apiKeysLoading;
    const isDataLoading = meteredUsageQuery.isLoading || usageOverTimeQuery.isLoading || auditLogsQuery.isLoading;
    const hasContract = !!contractId;
    const hasError = !!meteredUsageQuery.error || !!usageOverTimeQuery.error || !!auditLogsQuery.error;
    const errorMessage = getHttpResponseMessage(meteredUsageQuery.error) || getHttpResponseMessage(usageOverTimeQuery.error) || getHttpResponseMessage(auditLogsQuery.error) || "Failed to load utilization data.";
    return {
        activeApiKeys,
        avgDailyUsageValue,
        avgHourlyUsageValue,
        consumptionDateRange,
        consumptionUsageUnit: consumptionUsageData?.usageUnit || "bytes",
        currentApiKeysUsed: activeApiKeys.length,
        donutSegments,
        errorMessage,
        hasContract,
        hasError,
        isDataLoading,
        isFiltersLoading,
        keyNameMap,
        filterableApiKeysCount: filterableApiKeys.length,
        keyUsageTotals,
        lastUsedAt,
        peakPoint,
        selectableApiKeys,
        selectedApiKeyDetails,
        topTenKeys,
        totalConsumptionUsed: consumptionUsageData?.totalUsageValue || 0,
        totalRequests,
        totalUsageValue,
        trendPoints,
        usagePlan,
        usageUnit
    };
}
function getTotalRequestsFromUsageOverTime(usageData) {
    if (!usageData) {
        return undefined;
    }
    if ("hourlyUsage" in usageData) {
        return sumRecordCounts(usageData.hourlyUsage);
    }
    return sumRecordCounts(usageData.dailyUsage);
}
function sumRecordCounts(records) {
    let total = 0;
    let hasRecordCount = false;
    for (const record of records){
        if (!record || typeof record !== "object") {
            continue;
        }
        const fields = record;
        const rawRecordCount = fields["recordCount"] ?? fields["RecordCount"];
        if (typeof rawRecordCount === "number" && Number.isFinite(rawRecordCount)) {
            hasRecordCount = true;
            total += rawRecordCount;
        }
    }
    return hasRecordCount ? total : undefined;
}
export { useUtilizationDeepDiveData };
